import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import type { InventoryItem } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { specsToLine } from "@/lib/specs";
import { cn } from "@/lib/utils";
import { RecipePill } from "@/components/inventory/recipe-pill";

// Single-line label for the trigger: "Name · specs · (unit)".
export function itemLabel(it: InventoryItem): string {
  const specs = specsToLine(it.specs);
  const parts = [it.name];
  if (specs) parts.push(specs);
  parts.push(`(${it.unitOfMeasurement})`);
  return parts.join(" · ");
}

// Searchable item picker — replaces the bare <Select> so the operator can type
// to filter (the eligible list often runs 50–100 items with many same-named rows
// like "Chips · …" that are tellable apart only by brand / pack). Height-capped
// so the popover shows ~4 rows and the rest scrolls — the sheet never gets pushed
// off-screen. Shared across the stock-out forms (general consumption, with-limit,
// internal refill, pay-per-use).
interface ItemPickerProps {
  items: InventoryItem[];
  value: number | null;
  onChange: (id: number) => void;
  placeholder: string;
}

export function ItemPicker({ items, value, onChange, placeholder }: ItemPickerProps) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value) ?? null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate">{selected ? itemLabel(selected) : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0"
      >
        <Command>
          <CommandInput placeholder="Search by name, brand, or spec…" />
          {/* max-h ≈ 4 rows (44px × 4 + input). Anything past the
              cap scrolls — operator's sheet never gets pushed off-screen. */}
          <CommandList className="max-h-[200px]">
            <CommandEmpty>No matching items.</CommandEmpty>
            <CommandGroup>
              {items.map((it) => {
                const specs = specsToLine(it.specs);
                // Available inventory shown on the row. For items with variants
                // the on-hand total lives in variantStockTotal; otherwise it's
                // the item's own currentStock. Untracked items show nothing.
                // Both come off numeric() columns as strings at runtime (the
                // `number` TS type is contract-only — the client doesn't coerce),
                // so wrap in Number() or Number.isFinite() rejects "1.00" and the
                // badge silently vanishes for every non-variant item.
                const availNum =
                  it.inventoryTracked === false
                    ? null
                    : it.variantStockTotal != null
                      ? Number(it.variantStockTotal)
                      : it.currentStock != null
                        ? Number(it.currentStock)
                        : null;
                const avail =
                  availNum != null && Number.isFinite(availNum) ? availNum : null;
                return (
                  <CommandItem
                    key={it.id}
                    // Searchable string — name + brand + specs so a "Lays Magic
                    // Masala 48g" row matches "lays", "masala", "48", etc.
                    value={`${it.name} ${it.brand ?? ""} ${specs} ${it.unitOfMeasurement}`}
                    onSelect={() => {
                      onChange(it.id);
                      setOpen(false);
                    }}
                    className="py-2"
                  >
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm truncate">{it.name}</span>
                      {(specs || it.unitOfMeasurement) && (
                        <span className="text-[11px] text-muted-foreground truncate">
                          {[specs, `(${it.unitOfMeasurement})`].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                    {/* Phase 55 Plan 55-04 (RECIPE-DISPLAY-01): recipe items
                        never render an "N in stock" pill — they're cooked to
                        order (currentStock is permanently 0 by design). The
                        shared Recipe pill takes its place instead. */}
                    {it.hasRecipe ? (
                      <RecipePill className="ml-2 shrink-0" />
                    ) : (
                      avail != null && (
                        <span
                          className={cn(
                            "ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                            avail <= 0
                              ? "bg-destructive/10 text-destructive"
                              : "bg-emerald-50 text-emerald-700",
                          )}
                        >
                          {avail} in stock
                        </span>
                      )
                    )}
                    {value === it.id ? <Check className="ml-2 h-4 w-4 shrink-0" /> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
